
/**
 * FlowLab.
 *
 * The workspace is a client of the canonical API and of nothing else. It reads
 * an EngineeringModel by id and version, lists Generations by their own
 * indexed fields, and renders a Generation's surface from the one artifact the
 * platform serves for it. There is no job, no queue and no task: asking for a
 * mesh returns either the Generation that already exists for those inputs, or
 * the content key that will identify it when it does, and the key is what gets
 * polled. Nothing in this file has an id for something that is running, because
 * the platform does not name one.
 *
 * Everything that depends on a loaded document is built here rather than
 * written into index.html, so the vocabulary on screen is the platform's own:
 * the boundary rail comes from `boundary_groups`, the mesh panel's controls
 * come from `mesh-config.js` (which is transcribed field-for-field from
 * `MeshConfiguration`), and the result panel restates `MeshQualityReport`
 * without judging it.
 *
 * Reading order: state, then the three loaders (workspace, model, generation),
 * then the renderers, then the controls that drive them.
 */

import {
  ApiError,
  awaitGeneration,
  createGeneration,
  createModel,
  deriveInventory,
  downloadUrl,
  endpointsFor,
  fetchArtifacts,
  fetchSurface,
  getGeneration,
  getModel,
  listGenerations,
  listModelVersions,
  listModels,
  readConfig,
  uploadGeometry,
} from './api.js';
import { boundaryRows, byRole } from './boundary-view.js';
import * as fmt from './format.js';
import { artifactRows, historyRows, summarise } from './generation-view.js';
import {
  FIELDS,
  LAYER_DEFAULTS,
  LAYER_FIELDS,
  defaultConfiguration,
  fromStored,
  toRequest,
  validateConfiguration,
  validateField,
} from './mesh-config.js';
import { decodeSurface } from './surface-payload.js';
import { createScene } from './vtk-scene.js';
import './style.css';

const dom = {
  crumbModel: document.getElementById('crumb-model'),
  crumbGeneration: document.getElementById('crumb-generation'),
  chromeState: document.getElementById('chrome-state'),
  exportButton: document.getElementById('export'),

  railModel: document.getElementById('rail-model'),
  railGeometry: document.getElementById('rail-geometry'),
  railBoundary: document.getElementById('rail-boundary'),
  boundaryCount: document.getElementById('boundary-count'),
  railHistory: document.getElementById('rail-history'),
  historyCount: document.getElementById('history-count'),

  stage: document.getElementById('stage'),
  canvas: document.getElementById('canvas'),
  stageHint: document.getElementById('stage-hint'),

  viewtools: document.getElementById('viewtools'),
  colorSelect: document.getElementById('color-select'),
  toggleEdges: document.getElementById('toggle-edges'),
  toggleWireframe: document.getElementById('toggle-wireframe'),
  sliceEnable: document.getElementById('slice-enable'),
  sliceAxis: document.getElementById('slice-axis'),
  slicePosition: document.getElementById('slice-position'),
  slicePositionValue: document.getElementById('slice-position-value'),
  resetCamera: document.getElementById('reset-camera'),

  overlay: document.getElementById('overlay'),
  spinner: document.getElementById('spinner'),
  overlayMessage: document.getElementById('overlay-message'),
  overlayDetail: document.getElementById('overlay-detail'),
  overlayRetry: document.getElementById('overlay-retry'),

  selection: document.getElementById('selection'),
  tabMesh: document.getElementById('tab-mesh'),
  tabResult: document.getElementById('tab-result'),
  paneMesh: document.getElementById('pane-mesh'),
  paneResult: document.getElementById('pane-result'),

  statusCells: document.getElementById('status-cells'),
  statusFaces: document.getElementById('status-faces'),
  statusExtent: document.getElementById('status-extent'),
  statusSection: document.getElementById('status-section'),
  statusQuality: document.getElementById('status-quality'),
  statusRef: document.getElementById('status-ref'),
};

const DEFAULT_SLICE_PERCENT = 50;

/**
 * Advanced modules, in inspector order.
 *
 * Derived from `mesh-config.js`'s own `group` values rather than from a list of
 * modules someone would like to exist: a module with no fields behind it is a
 * heading that promises a capability the backend does not have.
 */
const MODULES = [
  { key: 'sizing', title: 'Sizing' },
  { key: 'algorithm', title: 'Algorithm' },
  { key: 'curvature', title: 'Curvature & features' },
  { key: 'layers', title: 'Boundary layers' },
  { key: 'quality', title: 'Quality limits' },
];

const state = {
  config: null,
  scene: null,
  sceneError: null,

  models: [],
  modelId: null,
  envelope: null, // {model_id, version, snapshot_hash, created_at, model}
  model: null,
  versions: [],

  history: [], // historyRows(...)
  generationId: null,
  generation: null,
  summary: null,
  surface: null,

  configuration: defaultConfiguration(),
  // Which disclosure sections are open. Advanced is closed on purpose: §9's
  // default experience is element size, layers, order and Generate.
  open: new Set(['layers']),
  tab: 'mesh',
  selection: null,
  generating: false,
  authoring: false,

  // Guards against an out-of-order response overwriting a newer request.
  loadToken: 0,
};

/* ------------------------------------------------------------ tiny helpers */

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined && text !== null) node.textContent = String(text);
  return node;
}

function clear(node) {
  while (node.firstChild) node.removeChild(node.firstChild);
}

function line(key, value, valueClass = 'rail__value') {
  const row = el('div', 'rail__line');
  row.append(el('span', 'rail__key', key), el('span', valueClass, value));
  return row;
}

function prop(key, value, valueClass = 'prop__value') {
  const row = el('div', 'prop');
  row.append(el('span', 'prop__key', key), el('span', valueClass, value));
  return row;
}

/** Trigger a download without navigating away from the workspace. */
function download(href) {
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = '';
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

/** A configuration key as the inspector spells it, for error text. */
function labelFor(key) {
  const [, index, layerKey] = /^boundary_layers\.(\d+)(?:\.(.+))?$/.exec(key) || [];
  if (index !== undefined) {
    const field = LAYER_FIELDS.find((candidate) => candidate.key === layerKey);
    return field ? `${field.label} (layer ${Number(index) + 1})` : `Boundary layer ${Number(index) + 1}`;
  }
  return FIELDS.find((candidate) => candidate.key === key)?.label || key;
}

function describe(error) {
  if (error instanceof ApiError && error.status) return `${error.message} (${error.status})`;
  return error?.message || String(error);
}

/* ------------------------------------------------------------------ overlay */

function showProgress(message, detail = '') {
  dom.overlay.hidden = false;
  dom.overlay.classList.remove('overlay--error');
  dom.spinner.hidden = false;
  dom.overlayMessage.textContent = message;
  dom.overlayDetail.textContent = detail;
  dom.overlayDetail.hidden = !detail;
  dom.overlayRetry.hidden = true;
}

function showError(message, detail = '', onRetry = null) {
  dom.overlay.hidden = false;
  dom.overlay.classList.add('overlay--error');
  dom.spinner.hidden = true;
  dom.overlayMessage.textContent = message;
  dom.overlayDetail.textContent = detail;
  dom.overlayDetail.hidden = !detail;
  dom.overlayRetry.hidden = !onRetry;
  dom.overlayRetry.onclick = onRetry;
}

function hideOverlay() {
  dom.overlay.hidden = true;
}

function setHint(text) {
  dom.stageHint.textContent = text || '';
  dom.stageHint.hidden = !text;
}

/** The transient band in the chrome. Never a job, never a queue position. */
function setChromeState(text) {
  dom.chromeState.textContent = text || '';
  dom.chromeState.hidden = !text;
}

/* ------------------------------------------------------------------- boot */

async function main() {
  try {
    state.config = readConfig();
  } catch (cause) {
    showError('FlowLab could not start', describe(cause));
    return;
  }

  wireControls();
  await startViewer();
  await loadWorkspace();
}

/**
 * Bring up the VTK runtime.
 *
 * A viewer failure is contained: the overlay lives inside the stage, so the
 * rail, the inspector and the metadata strip stay usable and the error offers
 * a retry rather than replacing the application with a message.
 */
async function startViewer() {
  showProgress('Loading the VTK runtime');
  try {
    state.scene = await createScene({
      canvas: dom.canvas,
      container: dom.stage,
      wasmBundleUrl: state.config.wasmBundleUrl,
      onProgress: (stage) => showProgress(stage),
    });
    state.sceneError = null;
    hideOverlay();
  } catch (cause) {
    state.scene = null;
    state.sceneError = cause;
    showError('The 3D viewer could not start', describe(cause), () => {
      startViewer().then(() => {
        if (state.scene && state.generationId) loadGeneration(state.generationId);
      });
    });
  }
}

/* ---------------------------------------------------------------- loading */

async function loadWorkspace() {
  try {
    state.models = await listModels(state.config);
  } catch (cause) {
    renderEverything();
    showError('Could not reach the FlowLab platform', describe(cause), () => loadWorkspace());
    return;
  }

  if (state.models.length === 0) {
    renderEverything();
    if (!state.sceneError) hideOverlay();
    setHint('No EngineeringModel yet. Ingest geometry and author a model to begin.');
    return;
  }

  // A pinned Generation decides which model is open, so a link to a result
  // lands on the model that produced it rather than on the newest one.
  // `?fresh=1` deliberately opens nothing: the tester picks a model or
  // uploads geometry themselves.
  let chosen = state.models[0];
  let pinned = null;
  if (state.config.fresh) {
    state.modelId = null;
    state.envelope = null;
    state.model = null;
    state.versions = [];
    state.history = [];
    state.generationId = null;
    state.generation = null;
    state.summary = summarise(null);
    state.surface = null;
    state.configuration = defaultConfiguration();
    renderEverything();
    if (!state.sceneError) hideOverlay();
    setHint('Fresh workspace. Upload geometry or pick a model to begin.');
    return;
  }
  if (state.config.generationId) {
    try {
      pinned = await getGeneration(state.config, state.config.generationId);
      const owner = await modelForSnapshot(pinned.generation?.model_snapshot_hash);
      if (owner) chosen = owner;
    } catch (cause) {
      setHint(`That Generation could not be opened: ${describe(cause)}`);
    }
  }

  await selectModel(chosen.model_id, { version: chosen.version, pinned });
}

/** Which model version produced a snapshot hash, if the platform still holds it. */
async function modelForSnapshot(snapshotHash) {
  if (!snapshotHash) return null;
  const direct = state.models.find((entry) => entry.snapshot_hash === snapshotHash);
  if (direct) return direct;
  for (const entry of state.models) {
    const versions = await listModelVersions(state.config, entry.model_id);
    const match = versions.find((version) => version.snapshot_hash === snapshotHash);
    if (match) return { ...entry, version: match.version };
  }
  return null;
}

async function selectModel(modelId, { version = undefined, pinned = null } = {}) {
  setHint('');
  try {
    state.envelope = await getModel(state.config, modelId, { version });
  } catch (cause) {
    showError('Could not load that model', describe(cause), () => selectModel(modelId, { version }));
    return;
  }
  state.modelId = modelId;
  state.model = state.envelope.model;
  state.selection = null;
  state.configuration = defaultConfiguration();

  try {
    state.versions = await listModelVersions(state.config, modelId);
  } catch {
    state.versions = [{ version: state.envelope.version, snapshot_hash: state.envelope.snapshot_hash }];
  }

  await loadHistory();

  // `?fresh=1` keeps the workspace empty for manual testing: history still
  // lists below, but nothing is selected or rendered until the tester picks a
  // Generation or uploads geometry. Otherwise the newest Generation opens, as
  // before.
  const target = state.config.fresh ? null : pinned?.generation_id || state.history[0]?.id || null;
  if (target) {
    await loadGeneration(target);
  } else {
    state.generationId = null;
    state.generation = null;
    state.summary = summarise(null);
    state.surface = null;
    renderEverything();
    if (!state.sceneError) hideOverlay();
    setHint('No mesh yet for this model. Set an element size and generate one.');
  }
}

/**
 * Every Generation of this model, across all its versions.
 *
 * A Generation references the *snapshot* it was built from, never the model
 * row, so the model's history is the union over its versions' hashes. One query
 * per version rather than a joined endpoint the platform does not offer.
 */
async function loadHistory() {
  const hashes = state.versions.length
    ? state.versions.map((entry) => entry.snapshot_hash)
    : [state.envelope.snapshot_hash];
  try {
    const batches = await Promise.all(
      hashes.map((hash) => listGenerations(state.config, { model_snapshot_hash: hash })),
    );
    const seen = new Set();
    const envelopes = [];
    for (const batch of batches) {
      for (const envelope of batch) {
        if (seen.has(envelope.generation_id)) continue;
        seen.add(envelope.generation_id);
        envelopes.push(envelope);
      }
    }
    state.history = historyRows(envelopes);
  } catch (cause) {
    state.history = [];
    setHint(`History unavailable: ${describe(cause)}`);
  }
}

async function loadGeneration(generationId) {
  const token = (state.loadToken += 1);
  state.generationId = generationId;
  state.surface = null;
  state.selection = null;
  renderEverything();

  const endpoints = endpointsFor(generationId);
  const scoped = { ...state.config, endpoints };

  let document_ = state.history.find((row) => row.id === generationId)?.generation || null;
  if (!document_) {
    try {
      const envelope = await getGeneration(state.config, generationId);
      document_ = envelope.generation;
    } catch (cause) {
      if (token !== state.loadToken) return;
      showError('Could not load that Generation', describe(cause));
      return;
    }
  }
  if (token !== state.loadToken) return;

  state.generation = document_;
  state.summary = summarise(document_);
  // Re-generating starts from exactly what ran, which is the record's own
  // configuration rather than a fresh set of defaults.
  state.configuration = fromStored(document_?.configuration);
  renderEverything();

  await loadSurface(scoped, token);
}

async function loadSurface(scoped, token) {
  if (!state.scene) return;
  if (!state.summary?.meshRef) {
    setHint(
      state.summary?.ok === false
        ? 'This Generation produced no mesh. Its error is in the Result panel.'
        : '',
    );
    dom.viewtools.hidden = true;
    return;
  }

  showProgress('Loading the mesh surface');
  try {
    const listing = await fetchArtifacts(scoped);
    if (token !== state.loadToken) return;
    if (listing.artifacts.length === 0) {
      hideOverlay();
      dom.viewtools.hidden = true;
      setHint('The renderable surface for this Generation is not available.');
      return;
    }

    const buffer = await fetchSurface(scoped);
    if (token !== state.loadToken) return;
    const surface = decodeSurface(buffer);
    state.scene.setSurface(surface);
    state.surface = surface;

    dom.viewtools.hidden = false;
    populateColorArrays();
    applyViewControls();
    hideOverlay();
    setHint('');
    renderStatus();
    renderRail();
  } catch (cause) {
    if (token !== state.loadToken) return;
    showError('The mesh surface could not be displayed', describe(cause), () =>
      loadSurface(scoped, token),
    );
  }
}

/* --------------------------------------------------------------- rendering */

function renderEverything() {
  renderCrumbs();
  renderRail();
  renderSelection();
  renderMeshPane();
  renderResultPane();
  renderStatus();
  renderExport();
}

function modelName() {
  const provenance = state.model?.geometry_provenance;
  if (provenance?.source_name) return provenance.source_name;
  if (state.modelId) return `Model ${String(state.modelId).slice(0, 8)}`;
  return null;
}

function renderCrumbs() {
  const name = modelName();
  dom.crumbModel.textContent = name
    ? `${name}${state.envelope ? ` · v${state.envelope.version}` : ''}`
    : 'No model';
  dom.crumbModel.className = name ? 'crumbs__item' : 'crumbs__item crumbs__item--empty';

  const row = state.history.find((entry) => entry.id === state.generationId);
  if (row) {
    dom.crumbGeneration.textContent = `Generation ${fmt.generationLabel(row.ordinal)}`;
    dom.crumbGeneration.className = 'crumbs__item crumbs__item--current';
  } else {
    dom.crumbGeneration.textContent = 'No generation';
    dom.crumbGeneration.className = 'crumbs__item crumbs__item--empty';
  }
}

function renderExport() {
  const has = Boolean(state.summary?.meshRef);
  dom.exportButton.disabled = !has;
  dom.exportButton.title = has
    ? 'Download the canonical mesh'
    : 'This Generation has no mesh to export';
}

function renderRail() {
  renderModelBlock();
  renderGeometryBlock();
  renderBoundaryBlock();
  renderHistoryBlock();
}

function renderModelBlock() {
  clear(dom.railModel);
  if (!state.model) {
    dom.railModel.append(el('p', 'rail__empty', state.models.length ? '—' : 'No models'));
    return;
  }

  if (state.models.length > 1) {
    const picker = el('select', 'field__input field__input--compact');
    for (const entry of state.models) {
      const option = el('option', null, entry.model?.geometry_provenance?.source_name || entry.model_id.slice(0, 8));
      option.value = entry.model_id;
      picker.append(option);
    }
    picker.value = state.modelId;
    picker.addEventListener('change', () => selectModel(picker.value));
    dom.railModel.append(picker);
  } else {
    dom.railModel.append(line('Name', modelName(), 'rail__value rail__value--strong'));
  }

  if (state.versions.length > 1) {
    const row = el('div', 'rail__line');
    row.append(el('span', 'rail__key', 'Version'));
    const picker = el('select', 'field__input field__input--compact');
    for (const entry of state.versions) {
      const option = el('option', null, `v${entry.version}`);
      option.value = String(entry.version);
      picker.append(option);
    }
    picker.value = String(state.envelope.version);
    picker.addEventListener('change', () =>
      selectModel(state.modelId, { version: Number(picker.value) }),
    );
    row.append(picker);
    dom.railModel.append(row);
  } else {
    dom.railModel.append(line('Version', `v${state.envelope.version}`));
  }

  dom.railModel.append(
    line('Units', state.model.units?.length || '—'),
    line('Frame', `${(state.model.coord_system?.up_axis || '?').toUpperCase()} up · ${state.model.coord_system?.handedness || '?'}`),
    line('Snapshot', fmt.digest(state.envelope.snapshot_hash), 'rail__value rail__value--mono'),
    line('Authored', fmt.when(state.envelope.created_at)),
  );
}

function renderGeometryBlock() {
  clear(dom.railGeometry);
  const provenance = state.model?.geometry_provenance;
  if (!state.model) {
    dom.railGeometry.append(el('p', 'rail__empty', '—'));
    return;
  }
  dom.railGeometry.append(
    line('Format', (provenance?.source_format || '—').toUpperCase()),
    line('Size', provenance ? fmt.bytes(provenance.byte_size) : '—'),
    line('Geometry', fmt.digest(state.model.geometry_ref), 'rail__value rail__value--mono'),
  );
  if (provenance?.inventory_ref) {
    dom.railGeometry.append(
      line('Inventory', fmt.digest(provenance.inventory_ref), 'rail__value rail__value--mono'),
    );
  }
}

/** The boundary rows currently on screen, from the model and the shown mesh. */
function currentRows() {
  return boundaryRows(state.model, state.generation?.boundary_map || null);
}

function renderBoundaryBlock() {
  clear(dom.railBoundary);
  const rows = currentRows();
  dom.boundaryCount.textContent = rows.length ? String(rows.length) : '';
  if (rows.length === 0) {
    dom.railBoundary.append(el('p', 'rail__empty', 'None authored'));
    return;
  }

  const highlightable = Boolean(state.scene?.supportsSelection && state.surface);
  for (const group of byRole(rows)) {
    dom.railBoundary.append(el('div', 'rail__role', fmt.slug(group.role)));
    for (const row of group.rows) {
      const button = el('button', 'group');
      button.type = 'button';
      if (row.id === state.selection) button.classList.add('group--selected');
      button.append(el('span', 'group__label', row.label));
      if (row.blocking) {
        button.append(el('span', `group__flag group__flag--${row.status}`, row.status));
      } else if (row.status === 'unbound') {
        button.append(el('span', 'group__flag group__flag--unbound', 'unbound'));
      }
      button.append(el('span', 'group__tags', row.tags.length ? row.tags.join(' ') : '—'));
      button.addEventListener('click', () => toggleSelection(row.id));
      dom.railBoundary.append(button);
    }
  }
}

function renderHistoryBlock() {
  clear(dom.railHistory);
  dom.historyCount.textContent = state.history.length ? String(state.history.length) : '';
  if (state.history.length === 0) {
    dom.railHistory.append(el('p', 'rail__empty', 'No generations'));
    return;
  }
  for (const row of state.history) {
    const button = el('button', 'gen');
    button.type = 'button';
    if (row.id === state.generationId) button.classList.add('gen--current');
    button.append(el('span', 'gen__ordinal', fmt.generationLabel(row.ordinal)));

    const body = el('div', 'gen__body');
    body.append(
      el('div', 'gen__cells', row.cells === null ? 'No mesh' : `${fmt.count(row.cells)} cells`),
      el('div', 'gen__meta', fmt.when(row.createdAt)),
    );
    button.append(body);
    if (row.status === 'failed') {
      button.append(el('span', 'gen__status gen__status--failed', 'failed'));
    }
    button.addEventListener('click', () => loadGeneration(row.id));
    dom.railHistory.append(button);
  }
}

/* ------------------------------------------------------------- inspector */

function renderSelection() {
  clear(dom.selection);
  if (!state.selection) {
    dom.selection.hidden = true;
    return;
  }
  const row = currentRows().find((candidate) => candidate.id === state.selection);
  if (!row) {
    dom.selection.hidden = true;
    return;
  }
  dom.selection.hidden = false;

  const head = el('div', 'selection__head');
  head.append(el('span', 'selection__label', row.label), el('span', 'selection__role', fmt.slug(row.role)));
  dom.selection.append(head);

  const source = row.fromGeneration ? 'from this Generation' : 'from the model binding';
  dom.selection.append(
    el('div', 'selection__meta', `${row.tags.length ? `tags ${row.tags.join(', ')}` : 'no entities'} · ${source}`),
  );
  if (row.reason) dom.selection.append(el('div', 'selection__note', row.reason));
  if (state.surface && state.scene && !state.scene.supportsSelection) {
    dom.selection.append(
      el('div', 'selection__note', 'This surface carries no entity ids, so it cannot be highlighted.'),
    );
  }
}

function section(key, title, { collapsible = true, badge = null } = {}) {
  const wrapper = el('section', 'section');
  const open = !collapsible || state.open.has(key);
  if (open) wrapper.classList.add('section--open');

  const head = el('button', collapsible ? 'section__head' : 'section__head section__head--static');
  head.type = 'button';
  const title_ = el('span', null, title);
  head.append(title_);
  const right = el('span', null);
  if (badge) right.append(el('span', 'rail__count', badge));
  if (collapsible) right.append(el('span', 'section__chevron', '▶'));
  head.append(right);

  const body = el('div', 'section__body');
  body.hidden = !open;
  if (collapsible) {
    head.addEventListener('click', () => {
      if (state.open.has(key)) state.open.delete(key);
      else state.open.add(key);
      renderMeshPane();
    });
  } else {
    head.disabled = true;
  }

  wrapper.append(head, body);
  return { wrapper, body };
}

function unitOf(field) {
  if (field.unit) return field.unit;
  if (field.kind === 'length') return state.model?.units?.length || null;
  return null;
}

/**
 * One property row.
 *
 * Committed on `change`, not on `input`: the panel is not re-rendered while a
 * number is being typed, so the caret stays where the user put it. The error
 * text and the Generate button are updated in place instead.
 */
function fieldRow(field, value, commit) {
  const row = el('div', 'field');
  const id = `cfg-${field.key}`;
  const label = el('label', 'field__label', field.label);
  label.htmlFor = id;
  row.append(label);

  const unit = unitOf(field);
  const control = el('div', `field__control${unit ? ' field__control--unit' : ''}`);
  let input;

  if (field.kind === 'choice') {
    input = el('select', 'field__input field__input--compact');
    for (const choice of field.choices) {
      const option = el('option', null, choice.label);
      option.value = String(choice.value);
      input.append(option);
    }
    input.value = String(value);
  } else {
    input = el('input', 'field__input field__input--compact');
    input.type = 'number';
    if (field.step !== undefined) input.step = String(field.step);
    if (field.min !== undefined) input.min = String(field.min);
    if (field.max !== undefined) input.max = String(field.max);
    input.placeholder = field.nullable ? 'unset' : '';
    input.value = value === null || value === undefined ? '' : String(value);
  }
  input.id = id;
  control.append(input);
  if (unit) control.append(el('span', 'field__unit', unit));
  row.append(control);

  const error = el('div', 'field__error');
  const problem = validateField(field, value);
  error.textContent = problem || '';
  if (problem) input.classList.add('field__input--invalid');
  row.append(error);
  if (field.hint) row.append(el('div', 'field__hint', field.hint));

  input.addEventListener('change', () => {
    let next;
    if (field.kind === 'choice') {
      const picked = field.choices.find((choice) => String(choice.value) === input.value);
      next = picked ? picked.value : value;
    } else {
      next = input.value === '' ? null : Number(input.value);
    }
    commit(next);
    const problemNow = validateField(field, next);
    error.textContent = problemNow || '';
    input.classList.toggle('field__input--invalid', Boolean(problemNow));
    refreshGenerate();
  });

  return row;
}

function configField(key) {
  const field = FIELDS.find((candidate) => candidate.key === key);
  return fieldRow(field, state.configuration[key], (next) => {
    state.configuration[key] = next;
  });
}

function renderMeshPane() {
  clear(dom.paneMesh);
  if (!state.model) {
    renderGeometryImportPane();
    return;
  }

  /* -------- the default experience: size, layers, order, generate -------- */

  const basic = section('mesh', 'Mesh', { collapsible: false });
  for (const field of FIELDS.filter((candidate) => candidate.basic && candidate.group === 'sizing')) {
    basic.body.append(configField(field.key));
  }

  const layersRow = el('div', 'field');
  layersRow.append(el('span', 'field__label', 'Boundary layers'));
  const layersControl = el('div', 'field__control');
  const layersSwitch = el('label', 'switch');
  const layersInput = el('input');
  layersInput.type = 'checkbox';
  layersInput.checked = state.configuration.boundary_layers.length > 0;
  layersInput.disabled = meshableGroups().length === 0;
  layersSwitch.append(layersInput, el('span', 'switch__track'), el('span', 'switch__text',
    layersInput.checked ? `${state.configuration.boundary_layers.length} group${state.configuration.boundary_layers.length === 1 ? '' : 's'}` : 'Off'));
  layersInput.addEventListener('change', () => {
    toggleAllLayers(layersInput.checked);
  });
  layersControl.append(layersSwitch);
  layersRow.append(layersControl);
  basic.body.append(layersRow);

  for (const field of FIELDS.filter((candidate) => candidate.basic && candidate.group === 'algorithm')) {
    basic.body.append(configField(field.key));
  }

  const generate = el('button', 'button button--primary');
  generate.type = 'button';
  generate.id = 'generate';
  generate.textContent = 'Generate';
  generate.addEventListener('click', generateMesh);
  const note = el('div', 'section__note');
  note.id = 'generate-note';
  basic.body.append(generate, note);
  dom.paneMesh.append(basic.wrapper);

  /* ------------------------------ advanced ------------------------------ */

  const advanced = section('advanced', 'Advanced options');
  for (const module of MODULES) {
    if (module.key === 'layers') {
      advanced.body.append(layerModule().wrapper);
      continue;
    }
    const fields = FIELDS.filter(
      (candidate) => candidate.group === module.key && !candidate.basic,
    );
    if (fields.length === 0) continue;
    const block = section(`advanced:${module.key}`, module.title);
    for (const field of fields) block.body.append(configField(field.key));
    advanced.body.append(block.wrapper);
  }
  dom.paneMesh.append(advanced.wrapper);

  refreshGenerate();
}

/**
 * The first authored object is still an EngineeringModel. Geometry ingest
 * establishes immutable bytes; this form asks for the units and frame that the
 * canonical model deliberately requires rather than assuming either from CAD.
 */
function renderGeometryImportPane() {
  const block = section('geometry-import', 'Begin with geometry', { collapsible: false });
  block.body.append(
    el('p', 'section__note',
      'Import CAD, declare its coordinate convention, then author the first EngineeringModel. Boundary groups are authored after geometry inventory is available.'),
  );

  const form = el('form', 'geometry-import');
  const fileRow = el('label', 'geometry-import__file');
  const fileInput = el('input', 'geometry-import__file-input');
  fileInput.type = 'file';
  fileInput.accept = '.step,.stp,.iges,.igs,.stl,.obj,.msh';
  fileInput.required = true;
  const fileName = el('span', 'geometry-import__file-name', 'Choose geometry');
  fileRow.append(fileInput, fileName);
  fileInput.addEventListener('change', () => {
    fileName.textContent = fileInput.files?.[0]?.name || 'Choose geometry';
  });
  form.append(fileRow, el('div', 'geometry-import__formats', 'STEP · IGES · STL · OBJ · MSH'));

  const units = authoringSelect('Length unit', 'mm', [
    ['m', 'metres'],
    ['mm', 'millimetres'],
    ['cm', 'centimetres'],
    ['in', 'inches'],
    ['ft', 'feet'],
  ]);
  const upAxis = authoringSelect('Up axis', 'z', [['x', 'X'], ['y', 'Y'], ['z', 'Z']]);
  const handedness = authoringSelect('Handedness', 'right', [['right', 'Right'], ['left', 'Left']]);
  form.append(units.row, upAxis.row, handedness.row);

  const action = el('button', 'button button--primary');
  action.type = 'submit';
  action.textContent = state.authoring ? 'Importing…' : 'Import geometry';
  action.disabled = state.authoring;
  const note = el('div', 'section__note');
  note.textContent = 'The uploaded file is retained by content address; file names are descriptive only.';
  form.append(action, note);
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const file = fileInput.files?.[0];
    if (!file || state.authoring) return;
    state.authoring = true;
    renderMeshPane();
    setChromeState('Importing geometry');
    try {
      const geometry = await uploadGeometry(state.config, file);
      const envelope = await createModel(state.config, {
        units: { length: units.input.value },
        coord_system: { up_axis: upAxis.input.value, handedness: handedness.input.value },
        geometry_ref: geometry.geometry_ref,
        boundary_groups: [],
        intent_params: {},
      });
      state.models = [envelope, ...state.models];
      setChromeState('Measuring geometry');
      const measured = await deriveInventory(state.config, envelope.model_id);
      state.models = [
        measured,
        ...state.models.filter((entry) => entry.model_id !== measured.model_id),
      ];
      setChromeState('');
      await selectModel(measured.model_id, { version: measured.version });
      setHint('Geometry imported and measured. Author boundary groups from its inventory before generating a mesh.');
    } catch (cause) {
      setChromeState('');
      showError('Geometry import was refused', describe(cause));
    } finally {
      state.authoring = false;
      if (!state.model) renderMeshPane();
    }
  });
  block.body.append(form);
  dom.paneMesh.append(block.wrapper);
}

function authoringSelect(label, value, choices) {
  const row = el('div', 'field');
  const input = el('select', 'field__input field__input--compact');
  input.value = value;
  for (const [optionValue, optionLabel] of choices) {
    const option = el('option', null, optionLabel);
    option.value = optionValue;
    input.append(option);
  }
  input.value = value;
  row.append(el('label', 'field__label', label), el('div', 'field__control'));
  row.lastChild.append(input);
  return { row, input };
}

/** Groups a mesh can actually be built against: bound, with entity tags. */
function meshableGroups() {
  return currentRows().filter((row) => row.status === 'bound' && row.tags.length > 0);
}

function specFor(groupId) {
  return state.configuration.boundary_layers.find((spec) => spec.groups.includes(groupId)) || null;
}

/**
 * Boundary layers, one spec per group.
 *
 * `BoundaryLayerSpec.groups` takes several ids, but one spec per group is the
 * expressive choice: it is still a valid configuration and it lets a wall and
 * an inlet carry different first-layer thicknesses, which sharing a spec cannot.
 */
function layerModule() {
  const groups = meshableGroups();
  const block = section('layers', 'Boundary layers', {
    badge: state.configuration.boundary_layers.length
      ? String(state.configuration.boundary_layers.length)
      : null,
  });

  if (groups.length === 0) {
    block.body.append(
      el('div', 'section__note', 'Boundary layers are specified per group. None of this model’s groups is bound to geometry.'),
    );
    return block;
  }

  for (const group of groups) {
    const spec = specFor(group.id);
    const row = el('div', 'field');
    const toggle = el('label', 'switch');
    const input = el('input');
    input.type = 'checkbox';
    input.checked = Boolean(spec);
    toggle.append(input, el('span', 'switch__track'), el('span', 'switch__text', group.label));
    input.addEventListener('change', () => {
      if (input.checked) {
        state.configuration.boundary_layers.push({ groups: [group.id], ...LAYER_DEFAULTS });
      } else {
        state.configuration.boundary_layers = state.configuration.boundary_layers.filter(
          (candidate) => !candidate.groups.includes(group.id),
        );
      }
      renderMeshPane();
    });
    row.append(toggle);
    if (group.id === state.selection) row.classList.add('field--selected');
    block.body.append(row);

    if (!spec) continue;
    for (const field of LAYER_FIELDS) {
      block.body.append(
        fieldRow(field, spec[field.key], (next) => {
          spec[field.key] = next;
        }),
      );
    }
  }
  return block;
}

function toggleAllLayers(on) {
  if (!on) {
    state.configuration.boundary_layers = [];
    renderMeshPane();
    return;
  }
  const groups = meshableGroups();
  // Walls first, because that is what a boundary layer is for; every bound
  // group when the model declares no wall, so the toggle is never a no-op.
  const walls = groups.filter((row) => row.role === 'wall');
  const target = walls.length ? walls : groups;
  state.configuration.boundary_layers = target.map((row) => ({
    groups: [row.id],
    ...LAYER_DEFAULTS,
  }));
  renderMeshPane();
}

/**
 * Enable or explain the one primary action.
 *
 * The blockers are the frontend reading of `assert_bindings_resolved`: the
 * backend refuses a stale or ambiguous binding with a 409, and saying so before
 * the press is the same statement made earlier. The geometry-inventory blocker
 * is the third refusal that same route makes, stated here for the same reason.
 */
function refreshGenerate() {
  const button = document.getElementById('generate');
  if (!button) return;
  const note = document.getElementById('generate-note');

  const problems = validateConfiguration(state.configuration);
  const blocked = currentRows().filter((row) => row.blocking);
  const reasons = [];
  const named = Object.keys(problems).map((key) => labelFor(key));
  if (named.length) reasons.push(`${named.join(', ')}: ${Object.values(problems)[0]}.`);
  if (blocked.length) {
    reasons.push(
      `${blocked.length} boundary group${blocked.length === 1 ? '' : 's'} must be re-bound before meshing: ${blocked
        .map((row) => row.label)
        .join(', ')}.`,
    );
  }

  // The third refusal POST /api/generations makes, and the one every model
  // authored in this workspace currently meets. Without a geometry inventory
  // the model records no ingested format, and the stored copy's suffix is
  // FlowLab's own choice, so nothing can locate the bytes to mesh.
  //
  // The remedy is named rather than the absence restated: POST
  // /api/models/{id}/inventory measures the stored geometry and appends the
  // version that records it. Import already calls it, so a model reaching here
  // without provenance is one whose measurement was refused or came from
  // elsewhere -- which is why the wording stays about the state, not the step.
  if (state.model && !state.model.geometry_provenance) {
    reasons.push(
      'This model carries no geometry inventory, so the platform cannot locate its geometry to mesh it. Derive one with POST /api/models/{id}/inventory; large CAD may exceed the measurement budget and is refused with geometry_measurement_timeout.',
    );
  }

  button.disabled = state.generating || reasons.length > 0;
  button.textContent = state.generating ? 'Generating…' : 'Generate';
  if (note) note.textContent = reasons.join(' ');
}

/* ----------------------------------------------------------- result pane */

function renderResultPane() {
  clear(dom.paneResult);
  const summary = state.summary;
  if (!summary || !summary.id) {
    dom.paneResult.append(el('p', 'rail__empty', 'No Generation selected.'));
    return;
  }

  const row = state.history.find((entry) => entry.id === summary.id);
  const parent = state.history.find((entry) => entry.id === summary.parentId);

  const head = section('result', row ? `Generation ${fmt.generationLabel(row.ordinal)}` : 'Generation', {
    collapsible: false,
  });
  const headline = el('div', 'headline');
  headline.append(
    el('span', 'headline__number', summary.cells === null ? '—' : fmt.count(summary.cells)),
    el('span', 'headline__unit', 'cells'),
  );
  head.body.append(headline);
  head.body.append(
    prop('Faces', state.surface ? fmt.count(state.surface.numberOfPolys) : '—'),
    prop('Points', state.surface ? fmt.count(state.surface.numberOfPoints) : '—'),
    prop('Dimension', summary.dimension === null ? '—' : `${summary.dimension}D`),
    prop(
      'Extent',
      summary.extent
        ? summary.extent.map((span) => fmt.metric(span)).join(' × ')
        : '—',
    ),
    prop('Status', summary.status, `prop__value prop__value--${summary.ok ? 'ok' : 'error'}`),
    prop('Source', parent ? `Generation ${fmt.generationLabel(parent.ordinal)}` : '—'),
  );
  dom.paneResult.append(head.wrapper);

  if (summary.error) dom.paneResult.append(failureBlock(summary.error).wrapper);
  dom.paneResult.append(qualityBlock(summary).wrapper);
  dom.paneResult.append(artifactBlock(summary).wrapper);
  dom.paneResult.append(provenanceBlock(summary).wrapper);
}

function failureBlock(error) {
  const block = section('failure', 'Failure', { collapsible: false });
  const card = el('div', 'failure');
  card.append(el('div', 'failure__code', error.code));
  card.append(el('div', 'failure__message', error.message));
  if (error.detail && Object.keys(error.detail).length) {
    const lines = Object.entries(error.detail).map(
      ([key, value]) => `${fmt.slug(key)}: ${typeof value === 'number' ? fmt.count(value) : value}`,
    );
    card.append(el('div', 'failure__detail', lines.join('\n')));
  }
  block.body.append(card);
  return block;
}

function qualityBlock(summary) {
  const block = section('quality', 'Quality', { collapsible: false, badge: summary.verdict.label });
  if (summary.metrics.length === 0 && summary.findings.length === 0) {
    block.body.append(
      el('div', 'section__note',
        summary.verdict.label === 'Not measured'
          ? 'No quality report: this Generation produced no mesh to measure.'
          : 'The mesher reported no metrics for this mesh.'),
    );
    return block;
  }

  if (summary.metrics.length) {
    const table = el('table', 'metrics');
    const head = el('tr');
    for (const title of ['Metric', 'Worst', 'Min', 'Max', 'Mean']) head.append(el('th', null, title));
    table.append(head);
    for (const metric of summary.metrics) {
      const tr = el('tr');
      tr.append(el('td', null, metric.name));
      tr.append(el('td', 'metrics__worst', fmt.metric(metric.worst)));
      tr.append(el('td', null, fmt.metric(metric.min)));
      tr.append(el('td', null, fmt.metric(metric.max)));
      tr.append(el('td', null, metric.mean === null || metric.mean === undefined ? '—' : fmt.metric(metric.mean)));
      table.append(tr);
    }
    block.body.append(table);
  }

  for (const finding of summary.findings) {
    const row = el('div', 'finding');
    row.append(el('span', `finding__severity finding__severity--${finding.severity}`, finding.severity));
    const body = el('div', 'finding__body');
    body.append(document.createTextNode(finding.message || ''));
    body.append(el('div', 'finding__code', finding.code));
    row.append(body);
    block.body.append(row);
  }

  const unmeasured = Object.entries(summary.unmeasured || {});
  if (unmeasured.length) {
    block.body.append(
      el('div', 'section__note',
        `Not measured: ${unmeasured.map(([name, why]) => `${name} (${why})`).join('; ')}`),
    );
  }
  if (summary.measuredBy) {
    block.body.append(el('div', 'section__note', `Measured by ${summary.measuredBy}`));
  }
  return block;
}

function artifactBlock(summary) {
  const block = section('artifacts', 'Artifacts', { collapsible: false });
  const endpoints = state.generationId ? endpointsFor(state.generationId) : null;
  for (const artifact of artifactRows(state.generation, endpoints)) {
    const row = el('div', 'artifact');
    const left = el('div');
    left.append(el('div', 'artifact__label', artifact.label));
    left.append(el('div', 'artifact__detail', artifact.ref ? fmt.digest(artifact.ref) : artifact.detail));
    row.append(left);
    if (artifact.href) {
      const link = el('a', 'artifact__link', 'Download');
      link.href = artifact.href;
      link.download = '';
      row.append(link);
    } else {
      row.append(el('span', 'artifact__absent', artifact.detail));
    }
    block.body.append(row);
  }
  if (!summary.meshRef) {
    block.body.append(el('div', 'section__note', 'A Generation with no mesh has nothing to download.'));
  }
  return block;
}

function provenanceBlock(summary) {
  const block = section('provenance', 'Provenance');
  block.body.append(
    prop('Mesh', summary.meshRef ? fmt.digest(summary.meshRef) : '—', 'prop__value prop__value--mono'),
    prop('Model snapshot', fmt.digest(summary.modelSnapshotHash), 'prop__value prop__value--mono'),
    prop('Input hash', fmt.digest(summary.inputHash), 'prop__value prop__value--mono'),
    prop('Geometry script', summary.geoScriptRef ? fmt.digest(summary.geoScriptRef) : '—', 'prop__value prop__value--mono'),
    prop('Produced by', summary.skill || '—'),
  );
  const configuration = summary.configuration;
  if (configuration) {
    const reuse = el('button', 'button button--quiet');
    reuse.type = 'button';
    reuse.textContent = 'Load this configuration';
    reuse.addEventListener('click', () => {
      state.configuration = fromStored(configuration);
      setTab('mesh');
      renderMeshPane();
    });
    block.body.append(reuse);
  }
  return block;
}

/* ------------------------------------------------------------- statusbar */

function renderStatus() {
  const summary = state.summary;
  dom.statusCells.textContent = summary?.cells === null || !summary ? '—' : `${fmt.count(summary.cells)} cells`;
  dom.statusFaces.textContent = state.surface ? `${fmt.count(state.surface.numberOfPolys)} faces` : '—';
  dom.statusExtent.textContent = summary?.extent
    ? `${summary.extent.map((span) => fmt.metric(span)).join(' × ')} ${state.model?.units?.length || ''}`.trim()
    : '—';

  const verdict = summary?.verdict;
  dom.statusQuality.textContent = verdict ? verdict.label : '—';
  dom.statusQuality.className = `statusbar__slot${
    verdict?.tone === 'error' ? ' statusbar__slot--error' : verdict?.tone === 'warn' ? ' statusbar__slot--warn' : ''
  }`;
  dom.statusRef.textContent = summary?.meshRef ? fmt.digest(summary.meshRef, 16) : '—';
  renderSectionStatus();
}

function renderSectionStatus() {
  if (!dom.sliceEnable.checked) {
    dom.statusSection.textContent = 'No section';
    return;
  }
  const plane = dom.sliceAxis.options[dom.sliceAxis.selectedIndex]?.textContent || '';
  dom.statusSection.textContent = `${plane} · ${dom.slicePosition.value}%`;
}

/* --------------------------------------------------------------- controls */

function toggleSelection(groupId) {
  state.selection = state.selection === groupId ? null : groupId;
  const row = state.selection ? currentRows().find((candidate) => candidate.id === state.selection) : null;

  if (state.scene && state.surface) {
    try {
      if (row && row.tags.length) {
        const matched = state.scene.setSelection(row.tags);
        if (matched === 0) {
          setHint(`“${row.label}” has no faces on this surface.`);
        } else {
          setHint('');
        }
      } else {
        state.scene.clearSelection();
        setHint('');
      }
    } catch (cause) {
      setHint(describe(cause));
    }
  }

  renderRail();
  renderSelection();
  renderMeshPane();
}

function setTab(tab) {
  state.tab = tab;
  dom.tabMesh.classList.toggle('tabs__tab--active', tab === 'mesh');
  dom.tabResult.classList.toggle('tabs__tab--active', tab === 'result');
  dom.paneMesh.hidden = tab !== 'mesh';
  dom.paneResult.hidden = tab !== 'result';
}

function populateColorArrays() {
  const previous = dom.colorSelect.value;
  clear(dom.colorSelect);
  const surface = el('option', null, 'Surface');
  surface.value = '';
  dom.colorSelect.append(surface);
  for (const array of state.scene?.arrays || []) {
    const option = el('option', null, `${array.name} (${array.location})`);
    option.value = array.name;
    dom.colorSelect.append(option);
  }
  dom.colorSelect.value = [...dom.colorSelect.options].some((option) => option.value === previous)
    ? previous
    : '';
}

/** Re-apply the view controls to a freshly loaded surface. */
function applyViewControls() {
  const scene = state.scene;
  if (!scene) return;
  scene.setColorArray(dom.colorSelect.value || null);
  scene.setEdgeVisibility(dom.toggleEdges.checked);
  scene.setWireframe(dom.toggleWireframe.checked);
  scene.setSlice({
    enabled: dom.sliceEnable.checked,
    axis: dom.sliceAxis.value,
    percent: Number(dom.slicePosition.value),
  });
  renderSectionStatus();
}

function wireControls() {
  dom.tabMesh.addEventListener('click', () => setTab('mesh'));
  dom.tabResult.addEventListener('click', () => setTab('result'));

  dom.exportButton.addEventListener('click', () => {
    if (!state.generationId || !state.summary?.meshRef) return;
    download(downloadUrl({ ...state.config, endpoints: endpointsFor(state.generationId) }));
  });

  dom.colorSelect.addEventListener('change', () => {
    state.scene?.setColorArray(dom.colorSelect.value || null);
  });
  dom.toggleEdges.addEventListener('change', () => {
    state.scene?.setEdgeVisibility(dom.toggleEdges.checked);
  });
  dom.toggleWireframe.addEventListener('change', () => {
    state.scene?.setWireframe(dom.toggleWireframe.checked);
  });

  dom.sliceEnable.addEventListener('change', () => {
    const on = dom.sliceEnable.checked;
    dom.sliceAxis.disabled = !on;
    dom.slicePosition.disabled = !on;
    state.scene?.setSlice({ enabled: on });
    renderSectionStatus();
  });
  dom.sliceAxis.addEventListener('change', () => {
    state.scene?.setSlice({ axis: dom.sliceAxis.value });
    renderSectionStatus();
  });
  dom.slicePosition.addEventListener('input', () => {
    const percent = Number(dom.slicePosition.value);
    dom.slicePositionValue.textContent = `${percent}%`;
    state.scene?.setSlice({ percent });
    renderSectionStatus();
  });
  dom.resetCamera.addEventListener('click', () => state.scene?.resetCamera());

  dom.slicePosition.value = String(DEFAULT_SLICE_PERCENT);
  dom.slicePositionValue.textContent = `${DEFAULT_SLICE_PERCENT}%`;
  setTab('mesh');
}

/* -------------------------------------------------------------- generate */

/**
 * Ask the platform for a mesh of the current configuration.
 *
 * 200 means the platform already holds a Generation for exactly these inputs
 * and hands it back; 202 means it accepted the request and returned the content
 * key the result will carry. Polling that key is how the result is found -- the
 * same value the dedup lookup used, which is why there is nothing here that
 * resembles tracking a job.
 */
async function generateMesh() {
  if (state.generating || !state.modelId) return;
  state.generating = true;
  refreshGenerate();
  setChromeState('Generating');

  try {
    const result = await createGeneration(state.config, {
      modelId: state.modelId,
      version: state.envelope.version,
      configuration: toRequest(state.configuration),
    });

    if (result.existing) {
      setChromeState('');
      await loadHistory();
      renderRail();
      await loadGeneration(result.generation.generation_id);
      setHint('This configuration had already been generated. Showing the existing result.');
      return;
    }

    setChromeState('Meshing');
    const envelope = await awaitGeneration(state.config, result.inputHash, {
      onTick: (attempt) => setChromeState(attempt > 8 ? 'Meshing — still working' : 'Meshing'),
    });
    setChromeState('');
    await loadHistory();
    await loadGeneration(envelope.generation_id);
    setTab('result');
    if (envelope.status === 'failed') {
      setHint('The mesh could not be built. Its error is in the Result panel.');
    }
  } catch (cause) {
    setChromeState('');
    const detail =
      cause instanceof ApiError && cause.body
        ? typeof cause.body === 'string'
          ? cause.body
          : JSON.stringify(cause.body, null, 2)
        : '';
    showError('The mesh request was refused', `${describe(cause)}${detail ? `\n\n${detail}` : ''}`);
  } finally {
    state.generating = false;
    refreshGenerate();
  }
}

main();
